import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play, Square, Lock, Unlock, Music } from "lucide-react";

const STAGES = [
  { key: "attune", label: "Attuning to your feeling", freq: 0 },
  { key: "baseline", label: "432 Hz calming baseline", freq: 432 },
  { key: "rhythm", label: "Rhythms of overcoming", freq: 396 },
];

// emotion -> gentle base frequency for the opening pad
const emoFreq = (emotion = "") => {
  const e = emotion.toLowerCase();
  if (/(anger|rage|frustrat)/.test(e)) return 288;
  if (/(fear|anx|panic|worry)/.test(e)) return 256;
  if (/(sad|grief|lonely|empty|down)/.test(e)) return 216;
  return 240;
};

const SoundSanctuary = ({ emotion }) => {
  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState(0);
  const [stay, setStay] = useState(false);
  const ctxRef = useRef(null);
  const nodesRef = useRef([]);
  const drumRef = useRef(null);
  const timerRef = useRef(null);
  const stayRef = useRef(false);

  useEffect(() => { stayRef.current = stay; }, [stay]);

  const clearNodes = () => {
    nodesRef.current.forEach((n) => { try { n.stop && n.stop(); n.disconnect(); } catch { /* noop */ } });
    nodesRef.current = [];
    if (drumRef.current) { clearInterval(drumRef.current); drumRef.current = null; }
  };

  const buildPad = (freq) => {
    const ctx = ctxRef.current;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(0.16, ctx.currentTime + 2); // slow fade-in / crossfade
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 900;
    [freq, freq * 1.003, freq * 0.5].forEach((f) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f;
      o.connect(lp); o.start(); nodesRef.current.push(o);
    });
    lp.connect(master); master.connect(ctx.destination);
    nodesRef.current.push(master, lp);
    return master;
  };

  const startDrums = () => {
    const ctx = ctxRef.current;
    drumRef.current = setInterval(() => {
      const t = ctx.currentTime;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.setValueAtTime(110, t); o.frequency.exponentialRampToValueAtTime(52, t + 0.18);
      g.gain.setValueAtTime(0.32, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
      o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t + 0.3);
    }, 700);
  };

  const goToStage = (i) => {
    clearNodes();
    setStage(i);
    const s = STAGES[i];
    buildPad(s.freq || emoFreq(emotion));
    if (s.key === "rhythm") startDrums();
  };

  const scheduleNext = (i) => {
    timerRef.current = setTimeout(() => {
      if (stayRef.current) { scheduleNext(i); return; } // locked — hold current stage
      if (i < STAGES.length - 1) { goToStage(i + 1); scheduleNext(i + 1); }
    }, 14000);
  };

  const start = () => {
    if (running) return;
    ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    setRunning(true);
    goToStage(0);
    scheduleNext(0);
  };

  const stop = () => {
    clearNodes();
    if (timerRef.current) clearTimeout(timerRef.current);
    setRunning(false); setStage(0); setStay(false);
  };

  useEffect(() => () => { clearNodes(); if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <div className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur-md p-6" data-testid="sound-sanctuary">
      <div className="flex items-center gap-2 text-teal-200/80 mb-1"><Music className="w-4 h-4" /><span className="text-xs uppercase tracking-wide">Sound Sanctuary</span></div>
      {emotion && <p className="text-white/60 text-sm">I sense some <span className="text-teal-200">{emotion}</span>. Let's move through it together.</p>}

      <div className="mt-4 flex items-center gap-3">
        {!running ? (
          <Button onClick={start} className="rounded-full bg-teal-200/20 hover:bg-teal-200/30 text-white border border-white/15" data-testid="sanctuary-start"><Play className="w-4 h-4 mr-2" /> Begin the journey</Button>
        ) : (
          <Button onClick={stop} className="rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/15" data-testid="sanctuary-stop"><Square className="w-4 h-4 mr-2" /> End</Button>
        )}
        {running && (
          <button onClick={() => setStay(!stay)} data-testid="stay-here-toggle"
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${stay ? "bg-teal-200/25 text-white border-teal-200/40" : "bg-transparent text-white/60 border-white/15 hover:text-white"}`}>
            {stay ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />} Stay Here
          </button>
        )}
      </div>

      {running && (
        <div className="mt-4 flex items-center gap-2" data-testid="sanctuary-stage">
          {STAGES.map((s, i) => (
            <div key={s.key} className={`flex-1 h-1.5 rounded-full transition-colors ${i <= stage ? "bg-teal-200/70" : "bg-white/10"}`} />
          ))}
        </div>
      )}
      {running && <p className="text-white/50 text-xs mt-2">{STAGES[stage].label}{stay ? " · locked" : ""}</p>}
    </div>
  );
};

export default SoundSanctuary;
